/**
 * Fuente única de verdad de entidades (Fase 4).
 *
 * Reglas:
 * - Los `@id` son globales y estables. Nunca se crea una segunda entidad
 *   Person / Organization / WebSite: se referencia por `@id`.
 * - Solo datos aprobados y visibles en el sitio. Nada inventado.
 * - `sameAs` contiene exclusivamente los perfiles enlazados públicamente
 *   desde el pie del sitio.
 */

export const SITE_URL = "https://luisroc.com";

export const ENTITY_IDS = {
  person: `${SITE_URL}/#luisroc`,
  organization: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
} as const;

/** Referencias reutilizables: nunca redeclarar la entidad completa. */
export const PERSON_REF = { "@id": ENTITY_IDS.person } as const;
export const ORG_REF = { "@id": ENTITY_IDS.organization } as const;
export const WEBSITE_REF = { "@id": ENTITY_IDS.website } as const;

/** NAP verificado (SEO-NAP-AUDIT.md). Única sede física. */
export const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "9002 Six Pines Dr, Suite 277",
  addressLocality: "Shenandoah",
  addressRegion: "TX",
  postalCode: "77380",
  addressCountry: "US",
} as const;

export const CONTACT = {
  telephone: "+1-832-521-5050",
  email: "luisroc@luisroc.com",
} as const;

/**
 * Experiencia aprobada por el propietario (Fase 4, cierre de confirmaciones).
 * Única redacción válida en todo el sitio. No usar 15+, 20+, 26+ ni variantes.
 */
export const EXPERIENCE = {
  es: "Más de 25 años de experiencia",
  en: "25+ years of experience",
  sinceEs: "Trabajando en marketing digital desde el año 2000.",
  sinceEn: "Working in digital marketing since 2000.",
  startYear: 2000,
} as const;

/** Áreas de experiencia respaldadas por páginas de servicio publicadas. */
export const KNOWS_ABOUT = [
  "Technical SEO",
  "Local SEO",
  "International SEO",
  "Ecommerce and Amazon SEO",
  "Google Ads and performance marketing",
  "Website strategy and redesign",
  "Hispanic market marketing",
  "Branding",
  "AI Search visibility",
] as const;

/** Perfiles oficiales enlazados públicamente desde el pie del sitio. */
export const PERSON_SAME_AS = [
  "https://www.linkedin.com/in/luisroc/",
  "https://www.youtube.com/@luisroc_seo",
  "https://www.facebook.com/LuisROC.SEO",
  "https://www.instagram.com/luisroc_us/",
] as const;

/**
 * Mercados atendidos (service area). NO son sedes: la única ubicación
 * física es Shenandoah, TX.
 */
export const AREA_SERVED = [
  { "@type": "City", name: "Houston", addressRegion: "TX" },
  { "@type": "City", name: "The Woodlands", addressRegion: "TX" },
  { "@type": "City", name: "Shenandoah", addressRegion: "TX" },
  { "@type": "City", name: "Spring", addressRegion: "TX" },
  { "@type": "City", name: "Conroe", addressRegion: "TX" },
  { "@type": "City", name: "Tomball", addressRegion: "TX" },
  { "@type": "City", name: "Magnolia", addressRegion: "TX" },
  { "@type": "City", name: "Katy", addressRegion: "TX" },
  { "@type": "State", name: "Texas" },
  { "@type": "State", name: "Florida" },
  { "@type": "Country", name: "United States" },
] as const;

/** Grafo global presente en todas las URLs. */
export const ENTITY_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": ENTITY_IDS.organization,
      name: "ROC Worldwide Agency LLC",
      legalName: "ROC Worldwide Agency LLC",
      url: SITE_URL,
      logo: `${SITE_URL}/media/luis-roc/favicon.png`,
      email: CONTACT.email,
      telephone: CONTACT.telephone,
      description:
        "Consultoría SEO y Search Growth para empresas B2B, e-commerce y marcas que compiten en Estados Unidos, en inglés y español.",
      address: POSTAL_ADDRESS,
      areaServed: AREA_SERVED,
      knowsLanguage: ["es", "en"],
      knowsAbout: KNOWS_ABOUT,
      founder: PERSON_REF,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        telephone: CONTACT.telephone,
        email: CONTACT.email,
        areaServed: "US",
        availableLanguage: ["es", "en"],
      },
    },
    {
      "@type": "Person",
      "@id": ENTITY_IDS.person,
      name: "Luis ROC",
      jobTitle: "SEO & Search Growth Consultant",
      url: `${SITE_URL}/sobre-mi`,
      image: `${SITE_URL}/media/luis-roc/portrait.png`,
      description:
        "Consultor de SEO y Search Growth con más de 25 años de experiencia, trabajando en marketing digital desde el año 2000. Trabaja en inglés y español con empresas B2B, e-commerce y marcas que compiten en Estados Unidos.",
      worksFor: ORG_REF,
      founderOf: ORG_REF,
      knowsLanguage: ["es", "en"],
      knowsAbout: KNOWS_ABOUT,
      sameAs: PERSON_SAME_AS,
    },
    {
      "@type": "WebSite",
      "@id": ENTITY_IDS.website,
      url: SITE_URL,
      name: "Luis ROC",
      publisher: ORG_REF,
      creator: PERSON_REF,
      inLanguage: ["es-US", "en-US"],
    },
  ],
};
